/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return */
import { parseSearchFiltersToUrl } from '~/apps/pu-links/infrastructure/searchUrlHelpers'
import { getTransformObject } from '~/common/photos/transforms'
import { getLangFallbackUrl } from '~/lang/utils/langfetch'
import { getWKTPointFromGeoPoint } from '~/utility/geo/GeoLocation'
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { keysToCamel } from '~/utility/toCamel'
import { Logger } from '../../../composables/useGtmLogRequest'
import {
  CampsitePriceSearchResult,
  Photo,
  PhotoTransforms,
  SearchFilters,
  campsitePriceSearchResultSchema,
} from '../schemas'

export class FetchCampsitePriceSearchService {
  constructor(private fetchOptions?: any) {}

  fetch(url: string, fetchOptions?: any): Promise<any> {
    return $fetch<any>(url, { ...this.fetchOptions, ...fetchOptions })
  }

  async search(
    langCode: string,
    filters: SearchFilters,
    _logger?: Logger,
    fetchOptions?: any,
  ) {
    const url = getLangFallbackUrl(
      langCode,
      `/search2/price/?${this.filtersToParams(filters).toString()}`,
    )
    const response = await this.fetch(url, fetchOptions)
    return this.formatResponse(response)
  }

  private formatResponse(response: any) {
    return {
      ...keysToCamel(response),
      campsites: this.formatResults(keysToCamel(response.results)),
      pagination: {
        currentPage: Number(response.page),
        totalPages: response.num_pages,
        totalCount: response.count,
      },
      title: response.title,
      heading: response.heading,
      description: response.description,
      hierarchyTrail: response.hierarchy_trail,
      bookable: response.bookable,
      bookableCount: response.count,
      category: response.category,
      count: response.count,
      path: response.path,
      leadPrice: response.lead_price,
      sortOptions: response.sort_options,
      sortOption: response.sort || response.sort_field,
      maxPrice: response.result_max_price,
      within: response.within,
      breadcrumbs:
        response.breadcrumbs?.map((breadcrumb: any) =>
          keysToCamel(breadcrumb),
        ) || [],
      facetHierarchy: response.facet_hierarchy || {},
      categoriesCounts: response.facet_category || {},
      hierarchyCounts: response.facet_hierarchy || {},
      keywordsCounts: response.facet_keywords || {},
      priceHistogram: response.price_histogram || undefined,
    }
  }

  private formatResults(results: any[]): CampsitePriceSearchResult[] {
    return results.map((campsite) =>
      campsitePriceSearchResultSchema.parse({
        ...campsite,
        id: campsite.campsiteId.toString(),
        availableToSearch: true,
        status: 'bookable',
        hierarchyPath: campsite.path,
        hierarchyStringShort: campsite.hierarchyTextShort,
        hierarchyStringShortCountry: campsite.hierarchyTextShortCountry,
        primaryPhoto: campsite.primaryPhoto
          ? this.mapPrimaryPhoto(campsite.primaryPhoto)
          : undefined,
        rating: Number(campsite.rating),
        rating10: Number(campsite.rating) * 2,
        point: campsite.point
          ? getWKTPointFromGeoPoint(getPointFromStringOrDefault(campsite.point))
          : undefined,
        nutshells: campsite.nutshells,
        distance:
          campsite.distance && Object.keys(campsite.distance).length
            ? campsite.distance
            : undefined,
      }),
    )
  }

  private mapPrimaryPhoto(photo: Photo) {
    return {
      caption: photo.caption,
      url: {
        ...getTransformObject<Omit<PhotoTransforms, 'masterImage'>>(
          'campsitePrimaryPhotoMaster',
          photo.url.masterImage,
        ),
        masterImage: photo.url.masterImage,
      },
    }
  }

  private filtersToParams(filters: SearchFilters) {
    if (!filters.dates) {
      throw new Error('no dates')
    }
    const params = new URLSearchParams()
    for (const param of parseSearchFiltersToUrl(filters)) {
      params.append(param.filterName, param.filterValue)
    }
    if (params.get('bounds')) {
      // remove bounds if path
      if (params.get('path')) {
        params.delete('bounds')
      }
    }
    if (filters.engine) {
      params.append('engine', filters.engine.toString())
    }
    params.append('num_bins', '100')
    params.delete('base')
    return params
  }
}
